import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Users, LogOut, Plus, Pencil, Trash2, Eye, Menu, X, Building, Image, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ImageManager } from '../components/ImageManager';
import { FloorplanManager } from '../components/FloorplanManager';
import { AIImageManager } from '../components/AIImageManager';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const propertyTypes = ['Appartamento', 'Villa', 'Mansarda', 'Terreno', 'Locale Commerciale', 'Ufficio', 'Box Auto', 'Posto Auto', 'Soluzione Semi-Indipendente', 'Soluzione Indipendente'];
const contractTypes = [
  { value: 'vendita', label: 'Vendita' },
  { value: 'affitto', label: 'Affitto' },
];
const energyClasses = ['A4', 'A3', 'A2', 'A1', 'B', 'C', 'D', 'E', 'F', 'G'];

const emptyProperty = {
  title: '', description: '', price: '', property_type: 'Appartamento', contract_type: 'vendita',
  city: '', address: '', area_sqm: '', external_area_sqm: '', rooms: '', bathrooms: '', floor: '',
  year_built: '', energy_class: '', ipe_value: '', parking: '', box_auto_sqm: '', posto_auto_sqm: '', has_elevator: false,
  cellar: false, cellar_sqm: '', terrace_sqm: '', garden_sqm: '', features: [], latitude: '', longitude: '',
  virtual_tour_url: '', video_url: '', featured: false,
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sellRequests, setSellRequests] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyProperty);
  const [saving, setSaving] = useState(false);
  const [featuresInput, setFeaturesInput] = useState('');
  const [geocoding, setGeocoding] = useState(false);

  // Home Settings state
  const [heroSettings, setHeroSettings] = useState({ hero_title: '', hero_subtitle: '', hero_image_url: '' });
  const [savingHero, setSavingHero] = useState(false);

  // Credentials change state
  const [credForm, setCredForm] = useState({ current_password: '', new_email: '', new_password: '' });
  const [savingCred, setSavingCred] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [propsRes, contactsRes, sellRes, servicesRes, statsRes, heroRes, notifsRes] = await Promise.all([
        axios.get(`${API}/properties`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true }),
        axios.get(`${API}/sell-requests`, { withCredentials: true }),
        axios.get(`${API}/service-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/home-settings`),
        axios.get(`${API}/notifications`, { withCredentials: true }),
      ]);
      setProperties(propsRes.data);
      setContacts(contactsRes.data);
      setSellRequests(sellRes.data);
      setServiceRequests(servicesRes.data);
      setStats(statsRes.data);
      setHeroSettings(heroRes.data);
      setNotifications(notifsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/admin'); };

  const openEditDialog = (property = null) => {
    if (property) {
      setEditingProperty(property);
      setFormData({
        ...property,
        price: property.price?.toString() || '',
        area_sqm: property.area_sqm?.toString() || '',
        external_area_sqm: property.external_area_sqm?.toString() || '',
        rooms: property.rooms?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        floor: property.floor != null ? property.floor.toString() : '',
        year_built: property.year_built?.toString() || '',
        latitude: property.latitude?.toString() || '',
        longitude: property.longitude?.toString() || '',
        ipe_value: property.ipe_value || '',
        parking: property.parking || '',
        box_auto_sqm: property.box_auto_sqm?.toString() || '',
        posto_auto_sqm: property.posto_auto_sqm?.toString() || '',
        has_elevator: property.has_elevator || false,
        cellar: property.cellar || false,
        cellar_sqm: property.cellar_sqm?.toString() || '',
        terrace_sqm: property.terrace_sqm?.toString() || '',
        garden_sqm: property.garden_sqm?.toString() || '',
      });
      setFeaturesInput(property.features?.join(', ') || '');
    } else {
      setEditingProperty(null);
      setFormData(emptyProperty);
      setFeaturesInput('');
    }
    setIsDialogOpen(true);
  };

  const handleGeocode = async () => {
    if (!formData.address || !formData.city) {
      toast.error('Inserisci indirizzo e città per la geocodifica');
      return;
    }
    setGeocoding(true);
    try {
      const { data } = await axios.get(`${API}/geocode`, {
        params: { address: formData.address, city: formData.city }
      });
      if (data.latitude && data.longitude) {
        setFormData({ ...formData, latitude: data.latitude.toString(), longitude: data.longitude.toString() });
        toast.success('Coordinate trovate!');
      } else {
        toast.error('Indirizzo non trovato');
      }
    } catch {
      toast.error('Errore nella geocodifica');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSaveProperty = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        area_sqm: parseFloat(formData.area_sqm) || 0,
        external_area_sqm: formData.external_area_sqm ? parseFloat(formData.external_area_sqm) : null,
        rooms: formData.rooms ? parseInt(formData.rooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        floor: formData.floor ? formData.floor.toString() : null,
        year_built: formData.year_built ? parseInt(formData.year_built) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        ipe_value: formData.ipe_value || null,
        parking: formData.parking && formData.parking !== 'none' ? formData.parking : null,
        box_auto_sqm: formData.box_auto_sqm ? parseFloat(formData.box_auto_sqm) : null,
        posto_auto_sqm: formData.posto_auto_sqm ? parseFloat(formData.posto_auto_sqm) : null,
        has_elevator: formData.has_elevator || false,
        cellar: formData.cellar || false,
        cellar_sqm: formData.cellar_sqm ? parseFloat(formData.cellar_sqm) : null,
        terrace_sqm: formData.terrace_sqm !== '' ? parseFloat(formData.terrace_sqm) : null,
        garden_sqm: formData.garden_sqm !== '' ? parseFloat(formData.garden_sqm) : null,
        features: featuresInput.split(',').map(f => f.trim()).filter(f => f),
      };

      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, payload, { withCredentials: true });
        toast.success('Annuncio aggiornato');
      } else {
        await axios.post(`${API}/properties`, payload, { withCredentials: true });
        toast.success('Annuncio creato');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo annuncio?')) return;
    try {
      await axios.delete(`${API}/properties/${id}`, { withCredentials: true });
      toast.success('Annuncio eliminato');
      fetchData();
    } catch { toast.error("Errore nell'eliminazione"); }
  };

  const handleImageUpload = async (propertyId, files) => {
    for (let i = 0; i < files.length; i++) {
      const fd = new FormData();
      fd.append('file', files[i]);
      try {
        await axios.post(`${API}/properties/${propertyId}/images`, fd, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch { toast.error(`Errore upload immagine ${i + 1}`); }
    }
    toast.success(`${files.length} immagine/i caricata/e`);
    fetchData();
  };

  const handleSaveHeroSettings = async () => {
    setSavingHero(true);
    try {
      await axios.put(`${API}/home-settings`, {
        hero_title: heroSettings.hero_title,
        hero_subtitle: heroSettings.hero_subtitle,
      }, { withCredentials: true });
      toast.success('Impostazioni Hero salvate');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSavingHero(false); }
  };

  const handleHeroImageUpload = async (files) => {
    const fd = new FormData();
    fd.append('file', files[0]);
    try {
      await axios.post(`${API}/home-settings/hero-image`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Immagine Hero aggiornata');
      fetchData();
    } catch { toast.error("Errore nell'upload"); }
  };

  const formatPrice = (price) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <div className="min-h-screen bg-background-alt" data-testid="admin-dashboard">
      <button className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-primary text-white rounded-md" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`fixed inset-y-0 left-0 w-64 admin-sidebar text-white transform transition-transform lg:translate-x-0 z-40 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <h2 className="font-serif text-xl mb-1">Green House</h2>
          <p className="text-white/60 text-sm font-sans">Admin Panel</p>
        </div>
        <nav className="px-4 space-y-1">
          <div className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-lg">
            <Building size={18} />
            <span className="font-sans text-sm">Dashboard</span>
          </div>
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"><Users size={18} /></div>
            <div>
              <p className="font-sans text-sm">{user?.name}</p>
              <p className="font-sans text-xs text-white/60">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10" onClick={handleLogout} data-testid="logout-button">
            <LogOut size={18} className="mr-2" /> Esci
          </Button>
        </div>
      </aside>

      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="font-serif text-3xl text-text-main">Dashboard</h1>
          <p className="font-sans text-text-muted">Gestisci i tuoi annunci immobiliari</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white p-6 rounded-xl">
              <p className="font-sans text-sm text-text-muted">Annunci Totali</p>
              <p className="font-serif text-3xl text-primary">{stats.total_properties}</p>
            </div>
            <div className="bg-white p-6 rounded-xl">
              <p className="font-sans text-sm text-text-muted">Richieste Contatto</p>
              <p className="font-serif text-3xl text-primary">{stats.total_contacts}</p>
              {stats.new_contacts > 0 && <Badge variant="destructive" className="mt-1">{stats.new_contacts} nuove</Badge>}
            </div>
            <div className="bg-white p-6 rounded-xl">
              <p className="font-sans text-sm text-text-muted">Richieste Vendita</p>
              <p className="font-serif text-3xl text-primary">{stats.total_sell_requests}</p>
              {stats.new_sell_requests > 0 && <Badge variant="destructive" className="mt-1">{stats.new_sell_requests} nuove</Badge>}
            </div>
            <div className="bg-white p-6 rounded-xl">
              <p className="font-sans text-sm text-text-muted">Vai al Sito</p>
              <Link to="/" className="font-sans text-primary hover:underline">Visualizza &rarr;</Link>
            </div>
          </div>
        )}

        <Tabs defaultValue="properties" className="bg-white rounded-xl shadow-sm">
          <TabsList className="w-full justify-start border-b rounded-none p-0 flex-wrap">
            <TabsTrigger value="properties" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">Annunci</TabsTrigger>
            <TabsTrigger value="contacts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">Contatti</TabsTrigger>
            <TabsTrigger value="sell-requests" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">Richieste Vendita</TabsTrigger>
            <TabsTrigger value="service-requests" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary" data-testid="tab-service-requests">Richieste Servizi</TabsTrigger>
            <TabsTrigger value="home-settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">Impostazioni</TabsTrigger>
            <TabsTrigger value="notifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary">Notifiche</TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties" className="p-6">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <h2 className="font-serif text-xl text-text-main">Gestione Annunci</h2>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm('Svuotare la cache foto? Le foto verranno ri-processate alla prossima visualizzazione. Utile se vedi foto sfocate o se hai sostituito foto esistenti.')) return;
                    try {
                      const { data } = await axios.post(`${API}/admin/cache/clear`, {}, { withCredentials: true });
                      toast.success(`Cache svuotata: ${data.memory_entries + data.disk_entries} voci rimosse`);
                    } catch {
                      toast.error('Errore durante lo svuotamento della cache');
                    }
                  }}
                  data-testid="clear-cache-btn"
                  title="Svuota la cache delle foto"
                >
                  Svuota cache foto
                </Button>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary hover:bg-primary-hover" onClick={() => openEditDialog()} data-testid="add-property-btn">
                      <Plus size={18} className="mr-2" /> Nuovo Annuncio
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">
                      {editingProperty ? 'Modifica Annuncio' : 'Nuovo Annuncio'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveProperty} className="space-y-4" data-testid="property-form">
                    <Input placeholder="Titolo *" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required data-testid="property-title" />
                    <Textarea placeholder="Descrizione *" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} required data-testid="property-description" />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <Input type="number" placeholder="Prezzo (€) *" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required data-testid="property-price" />
                      <Select value={formData.contract_type} onValueChange={(v) => setFormData({ ...formData, contract_type: v })}>
                        <SelectTrigger data-testid="property-contract-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{contractTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Select value={formData.property_type} onValueChange={(v) => setFormData({ ...formData, property_type: v })}>
                        <SelectTrigger data-testid="property-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{propertyTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input type="number" placeholder="Metratura Interna (m²) *" value={formData.area_sqm} onChange={(e) => setFormData({ ...formData, area_sqm: e.target.value })} required data-testid="property-area" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input type="number" placeholder="Metratura Esterna (m²)" value={formData.external_area_sqm} onChange={(e) => setFormData({ ...formData, external_area_sqm: e.target.value })} data-testid="property-ext-area" />
                      <Input type="number" placeholder="Vani" value={formData.rooms} onChange={(e) => setFormData({ ...formData, rooms: e.target.value })} data-testid="property-rooms" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input placeholder="Città *" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required data-testid="property-city" />
                      <Input placeholder="Indirizzo *" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} required data-testid="property-address" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input type="number" placeholder="Bagni" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} data-testid="property-bathrooms" />
                      <Input type="text" placeholder='Piano (es. "1", "2-3", "Terra", "1-2-3")' value={formData.floor} onChange={(e) => setFormData({ ...formData, floor: e.target.value })} data-testid="property-floor" />
                    </div>

                    {/* Box Auto / Posto Auto separati */}
                    <div className="border border-border rounded-lg p-4 space-y-3">
                      <p className="font-sans text-sm font-medium text-text-main">Parcheggio</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.parking?.includes('box_auto') || false}
                              onChange={(e) => {
                                const current = formData.parking ? formData.parking.split(',').filter(Boolean) : [];
                                const updated = e.target.checked ? [...current, 'box_auto'] : current.filter(v => v !== 'box_auto');
                                setFormData({ ...formData, parking: updated.join(',') || null, ...(e.target.checked ? {} : { box_auto_sqm: '' }) });
                              }}
                              className="w-4 h-4 accent-primary"
                              data-testid="parking-box"
                            />
                            <span className="font-sans text-sm">Box Auto</span>
                          </label>
                          <Input
                            type="number"
                            placeholder="Mq Box Auto"
                            value={formData.box_auto_sqm || ''}
                            onChange={(e) => setFormData({ ...formData, box_auto_sqm: e.target.value })}
                            disabled={!formData.parking?.includes('box_auto')}
                            data-testid="property-box-auto-sqm"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.parking?.includes('posto_auto') || false}
                              onChange={(e) => {
                                const current = formData.parking ? formData.parking.split(',').filter(Boolean) : [];
                                const updated = e.target.checked ? [...current, 'posto_auto'] : current.filter(v => v !== 'posto_auto');
                                setFormData({ ...formData, parking: updated.join(',') || null, ...(e.target.checked ? {} : { posto_auto_sqm: '' }) });
                              }}
                              className="w-4 h-4 accent-primary"
                              data-testid="parking-posto"
                            />
                            <span className="font-sans text-sm">Posto Auto</span>
                          </label>
                          <Input
                            type="number"
                            placeholder="Mq Posto Auto"
                            value={formData.posto_auto_sqm || ''}
                            onChange={(e) => setFormData({ ...formData, posto_auto_sqm: e.target.value })}
                            disabled={!formData.parking?.includes('posto_auto')}
                            data-testid="property-posto-auto-sqm"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Spazi Esterni e Accessori */}
                    <div className="border border-border rounded-lg p-4 space-y-3">
                      <p className="font-sans text-sm font-medium text-text-main">Spazi Esterni e Accessori</p>
                      <div className="grid grid-cols-2 gap-4">
                        <Input type="number" placeholder="Terrazzo (m²)" value={formData.terrace_sqm} onChange={(e) => setFormData({ ...formData, terrace_sqm: e.target.value })} data-testid="property-terrace" />
                        <Input type="number" placeholder="Giardino (m²)" value={formData.garden_sqm} onChange={(e) => setFormData({ ...formData, garden_sqm: e.target.value })} data-testid="property-garden" />
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <Input type="number" placeholder="Mq Cantinola" value={formData.cellar_sqm} onChange={(e) => setFormData({ ...formData, cellar_sqm: e.target.value })} data-testid="property-cellar-sqm" />
                      </div>
                      <div className="flex flex-wrap gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={formData.has_elevator} onChange={(e) => setFormData({ ...formData, has_elevator: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="property-elevator" />
                          <span className="font-sans text-sm">Ascensore</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={formData.cellar} onChange={(e) => setFormData({ ...formData, cellar: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="property-cellar" />
                          <span className="font-sans text-sm">Cantinola</span>
                        </label>
                      </div>
                      <p className="text-xs text-text-muted font-sans">Lascia vuoto i m² se non necessario.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input type="number" placeholder="Anno costruzione" value={formData.year_built} onChange={(e) => setFormData({ ...formData, year_built: e.target.value })} data-testid="property-year" />
                      <Select value={formData.energy_class} onValueChange={(v) => setFormData({ ...formData, energy_class: v })}>
                        <SelectTrigger data-testid="property-energy-class"><SelectValue placeholder="Classe Energetica" /></SelectTrigger>
                        <SelectContent>{energyClasses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input placeholder="IPE (kWh/m²a)" value={formData.ipe_value} onChange={(e) => setFormData({ ...formData, ipe_value: e.target.value })} data-testid="property-ipe" />
                    </div>
                    <Input placeholder="Caratteristiche (separate da virgola)" value={featuresInput} onChange={(e) => setFeaturesInput(e.target.value)} data-testid="property-features" />
                    
                    {/* Geocoding Section */}
                    <div className="border border-border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-sans text-sm font-medium text-text-main">Posizione sulla Mappa</p>
                        <Button type="button" variant="outline" size="sm" onClick={handleGeocode} disabled={geocoding} data-testid="geocode-btn">
                          <MapPin size={14} className="mr-1" />
                          {geocoding ? 'Ricerca...' : 'Trova coordinate'}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Input type="number" step="any" placeholder="Latitudine" value={formData.latitude} onChange={(e) => setFormData({ ...formData, latitude: e.target.value })} data-testid="property-latitude" />
                        <Input type="number" step="any" placeholder="Longitudine" value={formData.longitude} onChange={(e) => setFormData({ ...formData, longitude: e.target.value })} data-testid="property-longitude" />
                      </div>
                      <p className="text-xs text-text-muted font-sans">Inserisci indirizzo e città, poi clicca "Trova coordinate" per generare la mappa automaticamente.</p>
                    </div>

                    <Input placeholder="URL Video YouTube (embed)" value={formData.video_url} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} data-testid="property-video" />
                    <Input placeholder="URL / Iframe Virtual Tour 3D" value={formData.virtual_tour_url} onChange={(e) => setFormData({ ...formData, virtual_tour_url: e.target.value })} data-testid="property-virtual-tour" />
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="featured" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} className="w-4 h-4 accent-primary" data-testid="property-featured" />
                      <label htmlFor="featured" className="font-sans text-sm">In Evidenza sulla Home Page</label>
                    </div>
                    <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" disabled={saving} data-testid="property-save">
                      {saving ? 'Salvataggio...' : (editingProperty ? 'Aggiorna Annuncio' : 'Crea Annuncio')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">Foto</TableHead>
                    <TableHead>Titolo</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Città</TableHead>
                    <TableHead>Prezzo</TableHead>
                    <TableHead>Evidenza</TableHead>
                    <TableHead>Visite</TableHead>
                    <TableHead>Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8">Caricamento...</TableCell></TableRow>
                  ) : properties.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-text-muted">Nessun annuncio presente</TableCell></TableRow>
                  ) : (
                    properties.map((prop) => (
                      <TableRow key={prop.id} data-testid={`property-row-${prop.id}`}>
                        <TableCell>
                          {prop.images && prop.images.length > 0 ? (
                            <img
                              src={`${API}/files/${prop.images[0]}?size=thumb&v=3`}
                              alt={prop.title}
                              className="w-16 h-16 object-cover rounded-md border border-border"
                              data-testid={`property-thumb-${prop.id}`}
                            />
                          ) : (
                            <div className="w-16 h-16 rounded-md bg-background-alt border border-border flex items-center justify-center">
                              <Image size={18} className="text-text-muted" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">{prop.title}</TableCell>
                        <TableCell>{prop.property_type}</TableCell>
                        <TableCell>{prop.city}</TableCell>
                        <TableCell>{formatPrice(prop.price)}</TableCell>
                        <TableCell>{prop.featured ? <Badge className="bg-primary">Si</Badge> : <span className="text-text-muted text-sm">No</span>}</TableCell>
                        <TableCell>
                          <span className="font-sans font-medium text-primary">{prop.views || 0}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Link to={`/properties/${prop.id}`} target="_blank">
                              <Button variant="ghost" size="sm" title="Visualizza"><Eye size={16} /></Button>
                            </Link>
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(prop)} title="Modifica" data-testid={`edit-${prop.id}`}>
                              <Pencil size={16} />
                            </Button>
                            <ImageManager propertyId={prop.id} images={prop.images || []} onUpdate={fetchData} />
                            <FloorplanManager propertyId={prop.id} floorplans={prop.floorplans || []} onUpdate={fetchData} />
                            <AIImageManager propertyId={prop.id} aiImages={prop.ai_images || []} onUpdate={fetchData} />
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteProperty(prop.id)} title="Elimina" data-testid={`delete-${prop.id}`}>
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="p-6">
            <h2 className="font-serif text-xl text-text-main mb-6">Richieste di Contatto</h2>
            {contacts.length === 0 ? (
              <p className="text-center py-8 text-text-muted font-sans">Nessun contatto ricevuto</p>
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div key={c.id} className="bg-background-alt rounded-xl p-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant={c.status === 'new' ? 'destructive' : 'secondary'}>{c.status === 'new' ? 'Nuovo' : c.status}</Badge>
                        <span className="font-sans font-medium text-text-main">{c.name}</span>
                        <span className="font-sans text-xs text-text-muted">{formatDate(c.created_at)}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={async () => {
                        if (!window.confirm('Eliminare questo contatto?')) return;
                        try { await axios.delete(`${API}/contacts/${c.id}`, { withCredentials: true }); toast.success('Eliminato'); fetchData(); }
                        catch { toast.error('Errore'); }
                      }} data-testid={`delete-contact-${c.id}`}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2 text-sm font-sans">
                      <div><span className="text-text-muted">Email:</span> <a href={`mailto:${c.email}`} className="text-primary">{c.email}</a></div>
                      <div><span className="text-text-muted">Telefono:</span> <a href={`tel:${c.phone}`} className="text-primary">{c.phone}</a></div>
                      {c.property_id && <div><span className="text-text-muted">Immobile:</span> {c.property_id}</div>}
                    </div>
                    <p className="font-sans text-sm text-text-main bg-white p-3 rounded-lg">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Sell Requests Tab */}
          <TabsContent value="sell-requests" className="p-6">
            <h2 className="font-serif text-xl text-text-main mb-6">Richieste di Vendita</h2>
            {sellRequests.length === 0 ? (
              <p className="text-center py-8 text-text-muted font-sans">Nessuna richiesta</p>
            ) : (
              <div className="space-y-3">
                {sellRequests.map((r) => (
                  <div key={r.id} className="bg-background-alt rounded-xl p-5 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant={r.status === 'new' ? 'destructive' : 'secondary'}>{r.status === 'new' ? 'Nuovo' : r.status}</Badge>
                        <span className="font-sans font-medium text-text-main">{r.owner_name}</span>
                        <span className="font-sans text-xs text-text-muted">{formatDate(r.created_at)}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={async () => {
                        if (!window.confirm('Eliminare questa richiesta?')) return;
                        try { await axios.delete(`${API}/sell-requests/${r.id}`, { withCredentials: true }); toast.success('Eliminata'); fetchData(); }
                        catch { toast.error('Errore'); }
                      }} data-testid={`delete-sell-${r.id}`}>
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2 text-sm font-sans">
                      <div><span className="text-text-muted">Email:</span> <a href={`mailto:${r.email}`} className="text-primary">{r.email}</a></div>
                      <div><span className="text-text-muted">Telefono:</span> <a href={`tel:${r.phone}`} className="text-primary">{r.phone}</a></div>
                      <div><span className="text-text-muted">Tipo:</span> {r.property_type}</div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-2 text-sm font-sans">
                      <div><span className="text-text-muted">Città:</span> {r.city}</div>
                      <div><span className="text-text-muted">Indirizzo:</span> {r.address}</div>
                      <div><span className="text-text-muted">Superficie:</span> {r.area_sqm} m²</div>
                    </div>
                    {r.asking_price && <p className="font-sans text-sm"><span className="text-text-muted">Prezzo richiesto:</span> <span className="font-medium text-primary">{formatPrice(r.asking_price)}</span></p>}
                    {r.description && <p className="font-sans text-sm text-text-main bg-white p-3 rounded-lg">{r.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Service Requests Tab (APE, Catastali, Mutuo, etc.) */}
          <TabsContent value="service-requests" className="p-6">
            <h2 className="font-serif text-xl text-text-main mb-6">Richieste di Servizi</h2>
            <p className="font-sans text-sm text-text-muted mb-6">
              Richieste di consulenza: APE (Attestato Prestazione Energetica), Visure Catastali, Mutui e altri servizi.
            </p>
            {serviceRequests.length === 0 ? (
              <p className="text-center py-8 text-text-muted font-sans" data-testid="service-requests-empty">Nessuna richiesta servizio ricevuta</p>
            ) : (
              <div className="space-y-3" data-testid="service-requests-list">
                {serviceRequests.map((r) => (
                  <div key={r.id} className="bg-background-alt rounded-xl p-5 space-y-3" data-testid={`service-request-${r.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Badge variant={r.status === 'new' ? 'destructive' : 'secondary'}>
                          {r.status === 'new' ? 'Nuovo' : r.status}
                        </Badge>
                        <Badge variant="outline" className="border-primary text-primary">{r.service_type}</Badge>
                        <span className="font-sans font-medium text-text-main">{r.name}</span>
                        <span className="font-sans text-xs text-text-muted">{formatDate(r.created_at)}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm('Eliminare questa richiesta servizio?')) return;
                          try {
                            await axios.delete(`${API}/service-requests/${r.id}`, { withCredentials: true });
                            toast.success('Richiesta eliminata');
                            fetchData();
                          } catch {
                            toast.error('Errore durante l\'eliminazione');
                          }
                        }}
                        data-testid={`delete-service-${r.id}`}
                        title="Elimina richiesta"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-2 text-sm font-sans">
                      <div><span className="text-text-muted">Email:</span> <a href={`mailto:${r.email}`} className="text-primary">{r.email}</a></div>
                      <div><span className="text-text-muted">Telefono:</span> <a href={`tel:${r.phone}`} className="text-primary">{r.phone}</a></div>
                    </div>
                    {r.message && (
                      <div>
                        <p className="font-sans text-xs font-medium text-text-muted uppercase tracking-wider mb-2">Messaggio del cliente</p>
                        <p className="font-sans text-sm text-text-main bg-white p-4 rounded-lg whitespace-pre-wrap leading-relaxed border border-border">{r.message}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Home Settings Tab */}
          <TabsContent value="home-settings" className="p-6">
            <h2 className="font-serif text-xl text-text-main mb-6">Impostazioni Home Page</h2>
            <div className="max-w-xl space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block font-sans text-sm font-medium text-text-main mb-2">Titolo Hero</label>
                  <Input
                    value={heroSettings.hero_title || ''}
                    onChange={(e) => setHeroSettings({ ...heroSettings, hero_title: e.target.value })}
                    placeholder="Trova la casa dei tuoi sogni"
                    data-testid="hero-title-input"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-text-main mb-2">Sottotitolo Hero</label>
                  <Textarea
                    value={heroSettings.hero_subtitle || ''}
                    onChange={(e) => setHeroSettings({ ...heroSettings, hero_subtitle: e.target.value })}
                    placeholder="Green House ti accompagna..."
                    rows={3}
                    data-testid="hero-subtitle-input"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-text-main mb-2">Immagine di Sfondo Hero</label>
                  <div className="flex items-center gap-4">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => e.target.files && handleHeroImageUpload(e.target.files)}
                      />
                      <div className="inline-flex items-center px-4 py-2 border border-border rounded-md text-sm font-sans hover:bg-background-alt transition-colors cursor-pointer">
                        <Image size={16} className="mr-2" />
                        Carica immagine
                      </div>
                    </label>
                    {heroSettings.hero_image_url && (
                      <span className="text-xs text-primary font-sans">Immagine caricata</span>
                    )}
                  </div>
                </div>
                <Button onClick={handleSaveHeroSettings} className="bg-primary hover:bg-primary-hover" disabled={savingHero} data-testid="save-hero-settings">
                  {savingHero ? 'Salvataggio...' : 'Salva Impostazioni'}
                </Button>
              </div>
            </div>

            {/* Change Credentials Section */}
            <div className="max-w-xl mt-12 pt-8 border-t border-border">
              <h2 className="font-serif text-xl text-text-main mb-6">Modifica Credenziali di Accesso</h2>
              <div className="space-y-4">
                <div>
                  <label className="block font-sans text-sm font-medium text-text-main mb-2">Password Attuale *</label>
                  <Input
                    type="password"
                    value={credForm.current_password}
                    onChange={(e) => setCredForm({ ...credForm, current_password: e.target.value })}
                    placeholder="Inserisci la password attuale"
                    data-testid="cred-current-password"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-text-main mb-2">Nuova Email (lascia vuoto per non modificare)</label>
                  <Input
                    type="email"
                    value={credForm.new_email}
                    onChange={(e) => setCredForm({ ...credForm, new_email: e.target.value })}
                    placeholder="Nuova email di accesso"
                    data-testid="cred-new-email"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm font-medium text-text-main mb-2">Nuova Password (lascia vuoto per non modificare)</label>
                  <Input
                    type="password"
                    value={credForm.new_password}
                    onChange={(e) => setCredForm({ ...credForm, new_password: e.target.value })}
                    placeholder="Nuova password (min 6 caratteri)"
                    data-testid="cred-new-password"
                  />
                </div>
                <Button
                  onClick={async () => {
                    if (!credForm.current_password) { toast.error('Inserisci la password attuale'); return; }
                    if (!credForm.new_email && !credForm.new_password) { toast.error('Inserisci una nuova email o password'); return; }
                    setSavingCred(true);
                    try {
                      const payload = { current_password: credForm.current_password };
                      if (credForm.new_email) payload.new_email = credForm.new_email;
                      if (credForm.new_password) payload.new_password = credForm.new_password;
                      await axios.put(`${API}/auth/credentials`, payload, { withCredentials: true });
                      toast.success('Credenziali aggiornate con successo');
                      setCredForm({ current_password: '', new_email: '', new_password: '' });
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Errore nell\'aggiornamento');
                    } finally {
                      setSavingCred(false);
                    }
                  }}
                  className="bg-primary hover:bg-primary-hover"
                  disabled={savingCred}
                  data-testid="save-credentials"
                >
                  {savingCred ? 'Salvataggio...' : 'Aggiorna Credenziali'}
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-xl text-text-main">Notifiche Email</h2>
              <Button
                className="bg-primary hover:bg-primary-hover"
                onClick={async () => {
                  try {
                    const { data } = await axios.post(`${API}/admin/test-email`, {}, { withCredentials: true });
                    if (data.success) toast.success(data.message);
                    else toast.error(data.error);
                  } catch { toast.error('Errore nel test'); }
                }}
                data-testid="test-email-btn"
              >
                Invia Email di Test
              </Button>
            </div>
            <p className="font-sans text-sm text-text-muted mb-4">
              Ogni richiesta (contatto, vendita, servizio) genera una notifica qui sotto e tenta l'invio via email a info@greenhousecaserta.it
            </p>
            {notifications.length === 0 ? (
              <p className="text-center py-8 text-text-muted font-sans">Nessuna notifica</p>
            ) : (
              <div className="space-y-3">
                {notifications.map((n, i) => (
                  <div key={i} className="bg-background-alt rounded-xl p-4 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-sans font-medium text-text-main text-sm">{n.subject}</span>
                      <div className="flex items-center gap-2">
                        {n.email_sent ? (
                          <Badge className="bg-primary">Email Inviata</Badge>
                        ) : (
                          <Badge variant="secondary">Solo Dashboard</Badge>
                        )}
                        <span className="font-sans text-xs text-text-muted">{formatDate(n.created_at)}</span>
                      </div>
                    </div>
                    <p className="font-sans text-sm text-text-muted whitespace-pre-line">{n.body}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
